import { CloseOutlined } from '@ant-design/icons';
import { useMemoizedFn, useMount } from 'ahooks';
import { Checkbox, Form, Radio, Select, Switch } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch } from 'app/hooks';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { UnderlineText } from 'components/RevxTypography';
import {
  CustomPageCountRuleCodeEnum,
  QtyDependencyEnum,
  StepActionTypeEnum,
  UnitPricePricingMethod,
} from 'domains/skuDomain';
import { cloneDeep, uniqBy } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import skuService from 'services/skuService';
import { StepComponentProps } from '.';

const { Option } = Select;

/* the buyer is limited to these quantities while the price still comes from the pricing engine */
const CUSTOM_QUANTITY_SLOTS = 10;
const padCustomQuantities = (quantities?: number[]) => {
  const values: (number | undefined)[] = [...(quantities || [])];
  while (values.length < CUSTOM_QUANTITY_SLOTS) {
    values.push(undefined);
  }
  return values;
};

export const Step1QuantitiesOrientation = ({
  sku,
  topSku,
  skuPart,
}: StepComponentProps) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const maxNum = 99999;

  const [useUnitPriceMatrixFixedQuantity, setUseUnitPriceMatrixFixedQuantity] =
    useState<boolean>(false);

  const [showInsidePage, setShowInsidePage] = useState<Boolean>(false);
  const [fixedQuantities, setFixedQuantities] = useState<number[]>([]);
  const [customQuantityEnabled, setCustomQuantityEnabled] =
    useState<boolean>(false);
  const [customQuantityValues, setCustomQuantityValues] = useState<number[]>(
    []
  );

  /* what the buyer will be able to pick: both settings can be on, the unit price matrix wins when
     the sku really has one configured, otherwise the custom drop-down takes over */
  const quantityOptions = useMemo(() => {
    if (fixedQuantities.length > 0) {
      return fixedQuantities;
    }
    return customQuantityEnabled ? customQuantityValues : [];
  }, [fixedQuantities, customQuantityEnabled, customQuantityValues]);

  useEffect(() => {
    console.log(topSku);
  }, [topSku]);

  const QtyDependencyEnumArr = [
    {
      label: t('platform.sku.wizard.quantity.form.label.basedQuantity'),
      value: QtyDependencyEnum.WITH_EVEN_PAGE_COUNT,
    },
    {
      label: t('platform.sku.wizard.quantity.form.label.basedSkuPageCountMax'),
      value: QtyDependencyEnum.WITH_MAX_PAGE_COUNT,
    },
    {
      label: t(
        'platform.sku.wizard.quantity.form.label.basedSkupageCountEqual'
      ),
      value: QtyDependencyEnum.EQ_SKU_QTY,
    },
    {
      label: t('platform.sku.wizard.quantity.form.label.specificQuantity'),
      value: QtyDependencyEnum.SPECIFIC,
    },
  ];

  const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
  };

  const tailFormItemLayout = {
    wrapperCol: {
      span: 18,
      offset: 6,
    },
  };

  useMount(() => {
    dispatch.sku.setRevxSpinloading(true);

    try {
      let quantityValue: { [x: string]: any } = {
        defaultQty: sku?.defaultQty,
        defaultPageCount: sku?.defaultPageCount,
        pageCountEditable: sku?.pageCountEditable,
        sides: sku?.sides,
        orientation: sku?.orientation,
        customPageCountRuleCode:
          sku?.customPageCountRuleCode || CustomPageCountRuleCodeEnum.NONE,
        skipPageCount: Boolean(sku?.skipPageCount),
        qtyLocked: sku?.qtyLocked,
        useUnitPriceMatrixFixedQuantity: Boolean(
          sku?.useUnitPriceMatrixFixedQuantity
        ),
        hideQuantityMultipleVersion: Boolean(sku?.hideQuantityMultipleVersion),
        customQuantityEnabled: Boolean(sku?.customQuantityEnabled),
        customQuantities: padCustomQuantities(sku?.customQuantities),
      };
      if (topSku) {
        quantityValue = {
          qtyDependency: cloneDeep(skuPart?.qtyDependency),
          orientation: sku?.orientation,
          defaultPageCount: sku?.defaultPageCount,
          pageCountEditable: sku?.pageCountEditable,
          sides: sku?.sides,
          skipPageCount: Boolean(sku?.skipPageCount),
          qtyLocked: sku?.qtyLocked,
          useUnitPriceMatrixFixedQuantity: Boolean(
            sku?.useUnitPriceMatrixFixedQuantity
          ),
          hideQuantityMultipleVersion: Boolean(
            sku?.hideQuantityMultipleVersion
          ),
        };

        setUseUnitPriceMatrixFixedQuantity(
          Boolean(sku?.useUnitPriceMatrixFixedQuantity)
        );

        setShowInsidePage(
          skuPart?.qtyDependency?.qtyDependencyEnum !==
            QtyDependencyEnum.SPECIFIC
        );

        dispatch.sku.registerStepAction({
          [StepActionTypeEnum.STEP1_QUANTITIES]: individualSubmit,
        });
      } else {
        dispatch.sku.registerStepAction({
          [StepActionTypeEnum.STEP1_QUANTITIES]: submit,
        });
        setUseUnitPriceMatrixFixedQuantity(
          Boolean(sku?.useUnitPriceMatrixFixedQuantity)
        );
        setCustomQuantityEnabled(Boolean(sku?.customQuantityEnabled));
        setCustomQuantityValues(sku?.customQuantities || []);
      }

      form.setFieldsValue(quantityValue);

      dispatch.sku.setRevxSpinloading(false);
    } catch {
      dispatch.sku.setRevxSpinloading(false);
    }
  });

  useEffect(() => {
    (async () => {
      if (useUnitPriceMatrixFixedQuantity) {
        try {
          const res = await skuService.getBuyerUnitPrice(
            sku.draftFromId || sku.id || 0
          );
          if (
            res.pricingMethod === UnitPricePricingMethod.FIXED_QUANTITY &&
            res.fixedQuantity?.length > 0
          ) {
            const quantities = uniqBy(res.fixedQuantity, 'quantity').map(
              (i) => i.quantity
            );
            setFixedQuantities(quantities);
            const currentQty = form.getFieldValue('defaultQty');
            if (!quantities.includes(currentQty) && quantities.length > 0) {
              form.setFieldsValue({ defaultQty: quantities[0] });
            }
          } else {
            setFixedQuantities([]);
          }
        } catch (e) {
          console.error(e);
          setFixedQuantities([]);
        }
      } else {
        setFixedQuantities([]);
      }
    })();
  }, [useUnitPriceMatrixFixedQuantity, sku.draftFromId, sku.id, form]);

  const submit = useMemoizedFn(async () => {
    if (sku.readonly) {
      return true;
    }
    try {
      const formValue = await form.validateFields();
      formValue.customQuantities = (formValue.customQuantities || []).filter(
        (quantity: number) => Number(quantity) > 0
      );
      return dispatch.sku.updateQuantities({ ...sku, ...formValue });
    } catch {
      // do nothing when form invalid
      return false;
    }
  });

  const individualSubmit = useMemoizedFn(async () => {
    if (sku.readonly) {
      return true;
    }
    try {
      const formValue = await form.validateFields();
      return dispatch.sku.updateSkuPartQuantities({
        id: skuPart?.id,
        skuId: topSku?.id,
        version: sku.version,
        ...formValue,
      });
    } catch {
      // do nothing when form invalid
      return false;
    }
  });

  const qtyLock = (
    <Form.Item
      name="qtyLocked"
      label="Default quantity locked"
      valuePropName="checked"
    >
      <Switch />
    </Form.Item>
  );

  return (
    <>
      <div className="single-sku-config-wizard-step-title">
        {t('platform.sku.wizard.whatDefault')}{' '}
        <span className="brand-green">
          {' '}
          {t('platform.sku.wizard.qtyOrientation')}
        </span>
      </div>
      <div className="single-sku-config-wizard-step-content">
        <Form
          layout={'horizontal'}
          form={form}
          {...formItemLayout}
          autoComplete="off"
          onValuesChange={(values, allvalue) => {
            if (
              Object.keys(values).includes('useUnitPriceMatrixFixedQuantity')
            ) {
              setUseUnitPriceMatrixFixedQuantity(
                values.useUnitPriceMatrixFixedQuantity
              );
            }
            if (Object.keys(values).includes('customQuantityEnabled')) {
              setCustomQuantityEnabled(Boolean(values.customQuantityEnabled));
            }
            if (Object.keys(values).includes('customQuantities')) {
              setCustomQuantityValues(
                (allvalue.customQuantities || []).filter(
                  (quantity: number) => Number(quantity) > 0
                )
              );
            }
            if (
              allvalue?.qtyDependency?.qtyDependencyEnum !==
              QtyDependencyEnum.SPECIFIC
            ) {
              setShowInsidePage(true);
            } else {
              setShowInsidePage(false);
            }

            if (
              values?.qtyDependency?.qtyDependencyEnum ===
              QtyDependencyEnum.SPECIFIC
            ) {
              form.setFieldsValue({
                qtyDependency: {
                  qtyDependencyEnum: QtyDependencyEnum.SPECIFIC,
                  defaultQty: 1,
                  dependsOnSkuId: null,
                },
              });
            } else if (
              [
                QtyDependencyEnum.EQ_SKU_QTY,
                QtyDependencyEnum.WITH_EVEN_PAGE_COUNT,
                QtyDependencyEnum.WITH_MAX_PAGE_COUNT,
              ].includes(values?.qtyDependency?.qtyDependencyEnum)
            ) {
              form.setFieldsValue({
                qtyDependency: {
                  ...allvalue.qtyDependency,
                  defaultQty: '',
                },
              });
            }
          }}
        >
          {/* <Form.Item
            labelCol={{
              flex: 'none',
            }}
            wrapperCol={{
              flex: 'none',
            }}
            label={t('platform.sku.wizard.quantity.form.label.quantitySkip')}
            name="skipPageCount"
          >
            <Radio.Group disabled={sku.readonly || Boolean(topSku)}>
              <Radio value={true}>
                <span>Yes</span>
              </Radio>
              <Radio value={false}>
                <span>No</span>
              </Radio>
            </Radio.Group>
          </Form.Item> */}
          {topSku ? (
            <Form.Item
              label={t('platform.sku.wizard.quantity.form.label.qtyDependency')}
              name={['qtyDependency', 'qtyDependencyEnum']}
              rules={[{ required: true }]}
            >
              <Select
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
                disabled={sku.readonly}
                style={{ width: 300 }}
              >
                {QtyDependencyEnumArr.map((item) => (
                  <Option value={item.value} key={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <>
              <Form.Item
                label={''}
                name="useUnitPriceMatrixFixedQuantity"
                valuePropName="checked"
                {...tailFormItemLayout}
              >
                <Checkbox disabled={sku.readonly}>
                  {t(
                    'platform.sku.wizard.quantity.form.label.useUnitPriceMatrixFixedQuantity'
                  )}
                </Checkbox>
              </Form.Item>
              <Form.Item
                label={''}
                name="customQuantityEnabled"
                valuePropName="checked"
                {...tailFormItemLayout}
                style={{ marginBottom: 8 }}
              >
                <Checkbox disabled={sku.readonly}>
                  {t(
                    'platform.sku.wizard.quantity.form.label.customQuantityEnabled'
                  )}{' '}
                  <span style={{ color: '#8c8c8c' }}>
                    {t(
                      'platform.sku.wizard.quantity.form.label.customQuantityHint'
                    )}
                  </span>
                </Checkbox>
              </Form.Item>
              <Form.Item label={''} {...tailFormItemLayout}>
                <Form.List name="customQuantities">
                  {(fields, { add, remove }) => (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 8,
                        maxWidth: 520,
                      }}
                    >
                      {fields.map((field) => {
                        const filled =
                          Number(
                            form.getFieldValue(['customQuantities', field.name])
                          ) > 0;
                        return (
                          <div
                            key={field.key}
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <Form.Item
                              {...field}
                              noStyle
                              rules={[{ type: 'number', min: 1, max: maxNum }]}
                            >
                              <RevxInputNumber
                                isInteger
                                style={{ width: 78 }}
                                min={1}
                                max={maxNum}
                                disabled={sku.readonly}
                              />
                            </Form.Item>
                            {/* the space is always there so typing a value does not reflow the grid */}
                            <span
                              style={{
                                width: 16,
                                display: 'inline-flex',
                                justifyContent: 'center',
                              }}
                            >
                              {filled && !sku.readonly && (
                                <CloseOutlined
                                  style={{
                                    fontSize: 12,
                                    color: '#8c8c8c',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => {
                                    remove(field.name);
                                    /* keep the default positions available for the next quantity */
                                    if (
                                      fields.length <= CUSTOM_QUANTITY_SLOTS
                                    ) {
                                      add();
                                    }
                                  }}
                                />
                              )}
                            </span>
                          </div>
                        );
                      })}
                      {!sku.readonly && (
                        <UnderlineText color="green" onClick={() => add()}>
                          {t(
                            'platform.sku.wizard.quantity.form.label.addQuantity'
                          )}
                        </UnderlineText>
                      )}
                    </div>
                  )}
                </Form.List>
              </Form.Item>
              <Form.Item
                label={t(
                  'platform.sku.wizard.quantity.form.label.defaultQuantity'
                )}
                name="defaultQty"
                rules={[
                  { required: true },
                  { type: 'number', min: 1 },
                  {
                    validator: (_, value) => {
                      if (
                        !fixedQuantities.length &&
                        customQuantityEnabled &&
                        value &&
                        !customQuantityValues.includes(value)
                      ) {
                        return Promise.reject(
                          new Error(
                            t(
                              'platform.sku.wizard.quantity.form.label.customQuantityDefaultTip'
                            )
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                {quantityOptions.length > 0 ? (
                  <Select disabled={sku.readonly} style={{ width: 300 }}>
                    {quantityOptions.map((quantity) => (
                      <Option key={quantity} value={quantity}>
                        {quantity}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <RevxInputNumber
                    isInteger
                    style={{ width: 300 }}
                    min={1}
                    disabled={sku.readonly}
                    max={maxNum}
                  />
                )}
              </Form.Item>
              {qtyLock}
            </>
          )}

          {topSku && (
            <>
              {Boolean(showInsidePage) ? (
                <Form.Item
                  label={t(
                    'platform.sku.wizard.quantity.form.label.dependsOnSkuId'
                  )}
                  name={['qtyDependency', 'dependsOnSkuId']}
                  rules={[{ required: true }]}
                >
                  <Select
                    getPopupContainer={(e) => {
                      return e as HTMLElement;
                    }}
                    disabled={sku.readonly}
                    style={{ width: 300 }}
                  >
                    {(topSku?.parts || [])
                      .filter((item) => item.id !== skuPart?.id)
                      .map((item) => (
                        <Option value={item.part.id} key={item.part.id}>
                          {item.part.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              ) : (
                <>
                  <Form.Item
                    label={t(
                      'platform.sku.wizard.quantity.form.label.defaultQuantity'
                    )}
                    name={['qtyDependency', 'defaultQty']}
                    rules={[{ required: true }]}
                  >
                    <RevxInputNumber
                      isInteger
                      style={{ width: 300 }}
                      min={1}
                      disabled={sku.readonly}
                      max={maxNum}
                    />
                  </Form.Item>
                  {qtyLock}
                </>
              )}
            </>
          )}

          <Form.Item
            label={t(
              'platform.sku.wizard.quantity.form.label.defaultPageCount'
            )}
            name="defaultPageCount"
            rules={[{ required: true }, { type: 'number', min: 1 }]}
          >
            <RevxInputNumber
              isInteger
              style={{ width: 300 }}
              min={1}
              disabled={sku.readonly || Boolean(topSku)}
              max={maxNum}
            />
          </Form.Item>

          <Form.Item
            label={''}
            name="pageCountEditable"
            valuePropName="checked"
            {...tailFormItemLayout}
          >
            <Checkbox disabled={sku.readonly || Boolean(topSku)}>
              {t('platform.sku.wizard.quantity.form.label.pageCountTip')}
            </Checkbox>
          </Form.Item>

          <Form.Item
            label={''}
            name="customPageCountRuleCode"
            {...tailFormItemLayout}
          >
            <Radio.Group disabled={sku.readonly || Boolean(topSku)}>
              <Radio value={CustomPageCountRuleCodeEnum.NONE}>
                {CustomPageCountRuleCodeEnum.NONE}
              </Radio>
              <Radio value={CustomPageCountRuleCodeEnum.R4PG}>
                {CustomPageCountRuleCodeEnum.R4PG}
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.sku.wizard.quantity.form.label.numberSides')}
            name="sides"
            rules={[{ required: true }]}
          >
            <Radio.Group disabled={sku.readonly || Boolean(topSku)}>
              <Radio value={1}>1</Radio>
              <Radio value={2}>2</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={''}
            name="hideQuantityMultipleVersion"
            valuePropName="checked"
            {...tailFormItemLayout}
          >
            <Checkbox disabled={sku.readonly}>
              {t(
                'platform.sku.wizard.quantity.form.label.hideQuantityMultipleVersion'
              )}
            </Checkbox>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};
